import { assertUnreachable } from "./helpers/assertUnreachable";

const MATCHED_BROWSER_NAME = {
  chrome: undefined,
  safari: undefined,
  firefox: undefined,
  msie: undefined,
  trident: undefined,
} as const;

type MatchedBrowserName = keyof typeof MATCHED_BROWSER_NAME;

type BrowserName =
  | "Opera"
  | "Edge"
  | "Chrome"
  | "Safari"
  | "Firefox"
  | "MSIE"
  | "IE";
type SpecificBrowserNameVersion<T extends BrowserName> = `${T} ${number}`;
type BrowserNameVersion = SpecificBrowserNameVersion<BrowserName>;

function isMatchedBrowserName(
  browserName: string,
): browserName is MatchedBrowserName {
  return browserName in MATCHED_BROWSER_NAME;
}

function getBrowserNameAndVersion(
  userAgent: string,
): [MatchedBrowserName, `${number}`] | ["", ""] {
  const regExp = new RegExp(
    `(${Object.keys(MATCHED_BROWSER_NAME).join("|")})\\/?\\s*(\\d+)`,
    "i",
  );
  const match = userAgent.match(regExp);

  if (!match || !match[1] || !match[2]) {
    return ["", ""];
  }

  const browserName = match[1].toLowerCase();
  const browserVersion = Number.parseInt(match[2]);

  if (!isMatchedBrowserName(browserName) || Number.isNaN(browserVersion)) {
    return ["", ""];
  }

  return [browserName, `${browserVersion}`];
}

function combineBrowserNameVersion<T extends BrowserName>(
  browserName: T,
  browserVersion: `${number}`,
): SpecificBrowserNameVersion<T> {
  return `${browserName} ${browserVersion}`;
}

function getKnownChromiumBrowser(
  userAgent: string,
): SpecificBrowserNameVersion<"Opera" | "Edge"> | "" {
  const match = userAgent.match(/\b(OPR|Edg)\/(\d+)/);

  if (!match || !match[1] || !match[2]) {
    return "";
  }

  const browserName = match[1];
  const browserVersion = Number.parseInt(match[2]);

  if (Number.isNaN(browserVersion)) {
    return "";
  }

  let mappedBrowserName: "Opera" | "Edge";

  if (browserName === "OPR") {
    mappedBrowserName = "Opera";
  } else {
    mappedBrowserName = "Edge";
  }

  return combineBrowserNameVersion(mappedBrowserName, `${browserVersion}`);
}

function getSafariBrowser(
  userAgent: string,
  browserVersion: `${number}`,
): SpecificBrowserNameVersion<"Safari"> {
  const match = userAgent.match(/version\/(\d+)/i);
  const webKitVersion = Number.parseInt(match?.[1] ?? "");

  return combineBrowserNameVersion(
    "Safari",
    Number.isNaN(webKitVersion) ? browserVersion : `${webKitVersion}`,
  );
}

function getUnknownBrowser(appName: string, appVersion: string) {
  return `${appName} ${appVersion} -?`;
}

/**
 * Gets browser information as reported in User-Agent
 *
 * @return Browser information in one of the following formats
 * `"<browser_name> <version>"` | `"<appName> <appVersion> -?"`
 */
export function getBrowser(): BrowserNameVersion | string {
  const { appName, appVersion, userAgent } = navigator;
  const [matchedBrowserName, matchedBrowserVersion] =
    getBrowserNameAndVersion(userAgent);

  if (!matchedBrowserName) {
    return getUnknownBrowser(appName, appVersion);
  }

  switch (matchedBrowserName) {
    case "trident":
      // Trident is the IE layout engine, IE11 can only be detected with that
      return combineBrowserNameVersion("IE", "11");
    case "chrome":
      return (
        getKnownChromiumBrowser(userAgent) ||
        combineBrowserNameVersion("Chrome", matchedBrowserVersion)
      );
    case "safari":
      return getSafariBrowser(userAgent, matchedBrowserVersion);
    case "msie":
      return combineBrowserNameVersion("MSIE", matchedBrowserVersion);
    case "firefox":
      return combineBrowserNameVersion("Firefox", matchedBrowserVersion);
    default:
      assertUnreachable(matchedBrowserName);
  }

  return "";
}
